import * as THREE from 'three';

/** Same palette as the home-page / loader cube. */
const FACE_COLORS = [0x80bcbd, 0xaad9bb, 0xd5f0c1, 0xf9f7c9, 0xd9edbf, 0xdcffb7] as const;

/** Hits required to destroy (missile = full; railgun = 1 damage per hit → 2 hits). */
export const OBSTACLE_MAX_DAMAGE = 2;

/**
 * Coarse voxel grid (Three.js-style chunking: subdivide the box, drop cells).
 * 3³ keeps missing edge chunks large enough to read while flying.
 */
const VOXEL_RES = 3;

export function createObstacleCube(size = 6): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(size, size, size),
    FACE_COLORS.map((color) => new THREE.MeshBasicMaterial({ color })),
  );
  mesh.name = 'arcade-obstacle';
  mesh.userData.radius = size * 0.65;
  mesh.userData.size = size;
  mesh.userData.damage = 0;
  mesh.userData.spin = {
    x: 0.01 + Math.random() * 0.02,
    y: 0.01 + Math.random() * 0.025,
  };
  mesh.userData.baseColors = FACE_COLORS.slice();
  mesh.userData.damagedSolid = false;
  return mesh;
}

/** Clear damage voxel overlays parented under the obstacle. */
function clearDamageOverlays(mesh: THREE.Mesh) {
  const toRemove: THREE.Object3D[] = [];
  mesh.traverse((child) => {
    if (child !== mesh && child.name === 'obstacle-crack') {
      toRemove.push(child);
    }
  });
  for (const child of toRemove) {
    mesh.remove(child);
    if (child instanceof THREE.Mesh) {
      child.geometry.dispose();
      const mats = child.material;
      if (Array.isArray(mats)) {
        mats.forEach((m) => m.dispose());
      } else {
        mats.dispose();
      }
    }
  }
}

/**
 * First rail hit: replace solid cube with a 3×3×3 brick stack and delete
 * random corner/edge chunks so the block clearly “loses pieces” around the rim.
 *
 * Pattern follows common Three.js voxel demos: grid the mesh, then remove cells
 * rather than painting crack textures.
 */
export function applyObstacleDamageVisual(mesh: THREE.Mesh, damage: number) {
  const d = Math.max(0, Math.min(OBSTACLE_MAX_DAMAGE - 1, damage));
  mesh.userData.damage = d;

  clearDamageOverlays(mesh);

  const size = typeof mesh.userData.size === 'number' ? mesh.userData.size : 6;

  if (d <= 0) {
    if (mesh.userData.damagedSolid) {
      mesh.geometry.dispose();
      mesh.geometry = new THREE.BoxGeometry(size, size, size);
      mesh.userData.damagedSolid = false;
    }
    return;
  }

  // Solid hull → tiny placeholder; only voxel children draw
  if (!mesh.userData.damagedSolid) {
    mesh.geometry.dispose();
    mesh.geometry = new THREE.BoxGeometry(0.001, 0.001, 0.001);
    mesh.userData.damagedSolid = true;
  }

  const res = VOXEL_RES;
  const cell = size / res;
  const half = size * 0.5;
  // Visible seams between remaining bricks
  const brick = cell * 0.88;
  const maxI = res - 1;

  type Cell = [number, number, number];
  const corners: Cell[] = [];
  const edges: Cell[] = [];
  const faceCenters: Cell[] = [];
  const interior: Cell[] = [];

  for (let ix = 0; ix < res; ix++) {
    for (let iy = 0; iy < res; iy++) {
      for (let iz = 0; iz < res; iz++) {
        const onX = ix === 0 || ix === maxI;
        const onY = iy === 0 || iy === maxI;
        const onZ = iz === 0 || iz === maxI;
        const edgeCount = (onX ? 1 : 0) + (onY ? 1 : 0) + (onZ ? 1 : 0);
        const cellKey: Cell = [ix, iy, iz];
        if (edgeCount === 3) corners.push(cellKey);
        else if (edgeCount === 2) edges.push(cellKey);
        else if (edgeCount === 1) faceCenters.push(cellKey);
        else interior.push(cellKey);
      }
    }
  }

  const shuffle = <T>(arr: T[]) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const t = arr[i];
      arr[i] = arr[j];
      arr[j] = t;
    }
    return arr;
  };
  shuffle(corners);
  shuffle(edges);
  shuffle(faceCenters);

  const removed = new Set<string>();
  const mark = (c: Cell) => removed.add(`${c[0]},${c[1]},${c[2]}`);

  // Always bite several corners (most visible “lost chunks”)
  const cornerLose = 3 + Math.floor(Math.random() * 3); // 3–5 of 8
  for (let i = 0; i < cornerLose && i < corners.length; i++) {
    mark(corners[i]);
  }
  // Plus random edge pieces
  const edgeLose = 2 + Math.floor(Math.random() * 4); // 2–5
  for (let i = 0; i < edgeLose && i < edges.length; i++) {
    mark(edges[i]);
  }
  // Occasional face-center pock
  if (Math.random() > 0.4 && faceCenters.length) {
    mark(faceCenters[0]);
  }

  const baseColors: number[] = mesh.userData.baseColors ?? FACE_COLORS;

  for (let ix = 0; ix < res; ix++) {
    for (let iy = 0; iy < res; iy++) {
      for (let iz = 0; iz < res; iz++) {
        if (removed.has(`${ix},${iy},${iz}`)) {
          continue;
        }
        const color = baseColors[(ix + iy * 2 + iz * 3) % baseColors.length];
        const c = new THREE.Color(color);
        c.lerp(new THREE.Color(0xd07040), 0.18);
        const brickMesh = new THREE.Mesh(
          new THREE.BoxGeometry(brick, brick, brick),
          new THREE.MeshBasicMaterial({ color: c }),
        );
        brickMesh.name = 'obstacle-crack';
        brickMesh.position.set(
          -half + cell * (ix + 0.5),
          -half + cell * (iy + 0.5),
          -half + cell * (iz + 0.5),
        );
        const onEdge =
          ix === 0 || ix === maxI || iy === 0 || iy === maxI || iz === 0 || iz === maxI;
        if (onEdge) {
          // Slight jostle on remaining rim so it looks broken, not a perfect grid
          brickMesh.position.x += (Math.random() - 0.5) * cell * 0.06;
          brickMesh.position.y += (Math.random() - 0.5) * cell * 0.06;
          brickMesh.position.z += (Math.random() - 0.5) * cell * 0.06;
          brickMesh.rotation.set(
            (Math.random() - 0.5) * 0.2,
            (Math.random() - 0.5) * 0.2,
            (Math.random() - 0.5) * 0.2,
          );
        }
        mesh.add(brickMesh);
      }
    }
  }
}
